import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isNil, last } from 'ramda';
import PropTypes from 'prop-types';
import history from 'routing/history';
import {
  StyledStepper,
  Step,
  ProgressIndicator,
  StepLabel,
  SubSteps,
  SubStepLabel
} from './styled';

const Stepper = ({ steps, validSteps, selectedStep, claimType }) => {
  const { idCheckNeeded } = useSelector((state) => state.nomadInsurance.claims);

  // Do not show id verification step unless it is needed
  const filteredSteps = useMemo(() => {
    if (idCheckNeeded) {
      return steps;
    }
    return steps.filter((step) => step.path !== 'id-verification');
  }, [idCheckNeeded]);

  // order steps so that substeps are in an array within the "main" step
  const stepsToShow = useMemo(
    () =>
      filteredSteps.reduce((acc, cur) => {
        // clone to prevent mutating
        const clone = { ...cur };
        if (clone.isSubStep) {
          const lastStep = last(acc);
          if (!lastStep.subSteps) {
            lastStep.subSteps = [];
          }
          lastStep.subSteps.push(clone);
          return acc;
        }
        acc.push(clone);
        return acc;
      }, []),
    [filteredSteps]
  );

  const getCompletionPercentage = () => {
    const selectedMainStepIndex = stepsToShow.findIndex(
      (x) =>
        x.path === selectedStep?.path ||
        x.subSteps?.find((y) => y.path === selectedStep?.path)
    );
    const selectedSubStepIndex = stepsToShow[selectedMainStepIndex]?.subSteps?.findIndex(
      (y) => y.path === selectedStep?.path
    );
    const mainStepPercentage = (selectedMainStepIndex / (stepsToShow.length - 1)) * 100;
    if (isNil(selectedSubStepIndex) || selectedSubStepIndex === -1) {
      return mainStepPercentage;
    }
    const subStepPercentage =
      (selectedSubStepIndex + 1) /
      (stepsToShow[selectedMainStepIndex].subSteps?.length + 1);
    return mainStepPercentage + subStepPercentage * (1 / (stepsToShow.length - 1)) * 100;
  };

  return (
    <StyledStepper>
      {stepsToShow.map((step, index) => {
        const selectedStepIndex = steps.findIndex((x) => x.path === selectedStep?.path);
        const isConfirmation = selectedStepIndex === -1;
        const isStepSelected =
          selectedStep?.path === step.path ||
          step.subSteps?.find((x) => x.path === selectedStep?.path);
        const isStepValid = validSteps.indexOf(step.path) > -1;
        return (
          <React.Fragment key={index}>
            <Step
              $selected={isStepSelected}
              $isDone={!isStepSelected && (isStepValid || isConfirmation)}
            >
              <StepLabel
                $selected={selectedStep?.path === step.path}
                $isDone={isStepValid || isConfirmation}
                onClick={() => {
                  if (isStepValid) {
                    history.push(`/nomad-insurance/claim/${claimType}/${step.path}`);
                  }
                }}
              >
                {step.label}
              </StepLabel>
              <SubSteps>
                {step.subSteps?.map((step) => {
                  const isStepValid = validSteps.indexOf(step.path) > -1;
                  return (
                    <SubStepLabel
                      key={step.label}
                      $selected={selectedStep?.path === step.path}
                      $isDone={isStepValid || isConfirmation}
                      onClick={() => {
                        if (isStepValid) {
                          history.push(
                            `/nomad-insurance/claim/${claimType}/${step.path}`
                          );
                        }
                      }}
                    >
                      {step.label}
                    </SubStepLabel>
                  );
                })}
              </SubSteps>
            </Step>
          </React.Fragment>
        );
      })}
      <ProgressIndicator completionPercentage={getCompletionPercentage()} />
    </StyledStepper>
  );
};

Stepper.propTypes = {
  steps: PropTypes.array.isRequired,
  validSteps: PropTypes.array.isRequired,
  claimType: PropTypes.string.isRequired,
  selectedStep: PropTypes.object
};

export default Stepper;
